import React, { useState, useEffect } from 'react';
import Header from '../components/common/Header';

const VibeCodingStoryPage = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-purple-900/20"></div>
        </div>

        {/* Floating Code Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {['const vibe =', 'React.useState()', 'AI.collaborate()', 'DotPlayer.init()', 'dotstudioPRO.api', 'export default'].map((code, i) => (
            <div
              key={i}
              className="absolute text-blue-400/20 font-mono text-sm animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.5}s`
              }}
            >
              {code}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="relative z-10 text-center px-6 max-w-6xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="p-3 bg-blue-500/20 rounded-full">
                <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <div className="p-3 bg-purple-500/20 rounded-full">
                <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="p-3 bg-teal-500/20 rounded-full">
                <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              How We Built{' '}
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-teal-400 bg-clip-text text-transparent">
                ChillFree TV
              </span>
            </h1>
            
            <h2 className="text-2xl md:text-4xl font-light text-blue-200 mb-8">
              The Vibe Coding Story
            </h2>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed">
              The revolutionary AI-human collaboration that created a Netflix-level streaming platform 
              using React 19, cutting-edge development practices, and the philosophy of vibe coding.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <button 
              onClick={() => scrollToSection('introduction')}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-8 py-4 text-lg font-semibold rounded-full transition-all duration-300 transform hover:scale-105"
            >
              Explore the Journey
            </button>
            
            <button className="border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white px-8 py-4 text-lg font-semibold rounded-full transition-all duration-300">
              Watch Demo
            </button>
          </div>

          {/* Scroll Indicator */}
          <div className="flex flex-col items-center text-gray-400 animate-bounce">
            <span className="text-sm mb-2">Scroll to explore</span>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>

        {/* Gradient Overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-900 to-transparent"></div>
      </section>

      {/* Introduction Section */}
      <section id="introduction" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Redefining Development
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              ChillFree TV represents a new paradigm in software development, where AI and human creativity 
              combine to create experiences that feel as natural as breathing.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: "❤️",
                title: "Vibe Coding",
                description: "Writing code that captures and amplifies the intended user experience, prioritizing emotional connection over pure functionality.",
                color: "text-red-400"
              },
              {
                icon: "🎯",
                title: "The Challenge",
                description: "Building a Netflix-level streaming platform that competes with industry giants while delivering superior user experience.",
                color: "text-blue-400"
              },
              {
                icon: "🧠",
                title: "AI-Human Collaboration",
                description: "Revolutionary partnership between human creativity and AI capabilities, maintaining context across development sessions.",
                color: "text-purple-400"
              },
              {
                icon: "💡",
                title: "The Solution",
                description: "Combining React 19, dotstudioPRO API, and innovative development practices to create seamless streaming experiences.",
                color: "text-yellow-400"
              }
            ].map((concept, index) => (
              <div key={concept.title} className="bg-gray-800/50 border border-gray-700 hover:bg-gray-800/70 transition-all duration-300 rounded-lg p-6 h-full group">
                <div className="flex flex-col items-center text-center">
                  <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {concept.icon}
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {concept.title}
                  </h3>
                  
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {concept.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Philosophy Quote */}
          <div className="mt-20 text-center">
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl p-8 border border-blue-500/20">
              <blockquote className="text-2xl md:text-3xl font-light text-white mb-4 italic">
                "Vibe coding isn't just about writing functional software—it's about creating 
                digital experiences that understand and amplify human emotion."
              </blockquote>
              <cite className="text-blue-400 font-semibold">— The ChillFree TV Development Philosophy</cite>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              The Collaborative Team
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              A revolutionary partnership between human creativity and AI capabilities, 
              working together through Git repositories and persistent context.
            </p>
          </div>

          {/* Team Members */}
          <div className="grid lg:grid-cols-4 gap-8 mb-16">
            {[
              {
                name: "Joe Pascual",
                role: "Human Developer",
                icon: "👨‍💻",
                description: "Strategic vision, user experience design, and local development using Cursor IDE. Brings human creativity and emotional intelligence to the development process.",
                skills: ["Product Vision", "UX Design", "Cursor IDE", "Strategic Planning"],
                color: "from-blue-500 to-cyan-500",
                perspective: "Focuses on user needs, emotional design, and the overall vision of what ChillFree TV should feel like to users."
              },
              {
                name: "Matt Armstrong",
                role: "Backend Systems & API Developer",
                icon: "⚙️",
                description: "Backend systems architecture, API development, and Cursor IDE vibe coding. Ensures robust data flow and seamless integration between frontend and backend services.",
                skills: ["Backend Systems", "API Development", "Cursor IDE", "Data Architecture"],
                color: "from-orange-500 to-red-500",
                perspective: "Builds the invisible foundation that makes everything work smoothly, focusing on performance, reliability, and seamless data integration."
              },
              {
                name: "Manus AI",
                role: "AI Development Partner",
                icon: "🤖",
                description: "Persistent context, rapid implementation, and architectural consistency. Maintains deep project understanding across multiple development sessions.",
                skills: ["Code Generation", "Context Retention", "Architecture", "Implementation"],
                color: "from-purple-500 to-pink-500",
                perspective: "Maintains project context, implements complex features rapidly, and ensures consistency across the entire codebase."
              },
              {
                name: "Debug Agent",
                role: "Specialist AI",
                icon: "🔧",
                description: "Specialized debugging, architectural problem-solving, and technical optimization. Provides expert analysis for complex technical challenges.",
                skills: ["Debugging", "Performance", "Architecture", "Problem Solving"],
                color: "from-green-500 to-teal-500",
                perspective: "Tackles complex technical challenges, optimizes performance, and provides specialized expertise for difficult problems."
              }
            ].map((member, index) => (
              <div key={member.name} className="bg-gray-900/50 border border-gray-600 hover:border-gray-500 transition-all duration-300 rounded-lg p-6 h-full group">
                <div className="text-center mb-6">
                  <div className={`w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r ${member.color} p-0.5`}>
                    <div className="w-full h-full bg-gray-900 rounded-full flex items-center justify-center text-3xl">
                      {member.icon}
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {member.name}
                  </h3>
                  
                  <span className="inline-block bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm mb-4">
                    {member.role}
                  </span>
                </div>

                <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                  {member.description}
                </p>

                <div className="mb-6">
                  <h4 className="text-white font-semibold mb-3">Key Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {member.skills.map((skill) => (
                      <span key={skill} className="border border-gray-600 text-gray-300 px-2 py-1 rounded text-xs">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-800/50 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                    </svg>
                    Perspective
                  </h4>
                  <p className="text-gray-300 text-sm">
                    {member.perspective}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Collaboration Flow */}
          <div className="bg-gray-900/50 rounded-2xl p-8 border border-gray-600">
            <h3 className="text-2xl font-bold text-white mb-8 text-center">
              Collaboration Workflow
            </h3>
            
            <div className="flex flex-wrap justify-center items-center gap-4">
              {["Concept & Vision", "Backend Architecture", "AI Implementation", "Testing & Refinement", "Specialized Debugging", "Integration & Polish"].map((step, index, array) => (
                <div key={step} className="flex items-center">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg px-4 py-2">
                    <span className="text-white font-medium text-sm">{step}</span>
                  </div>
                  {index < array.length - 1 && (
                    <svg className="w-5 h-5 text-gray-400 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-300">
                This iterative process happens continuously through Git collaboration, 
                with Joe providing vision and UX direction, Matt building robust backend systems, 
                Manus AI implementing features rapidly, and the Debug Agent solving complex challenges 
                to create a seamless development experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">More Coming Soon</h2>
          <p className="text-xl text-gray-300 mb-8">
            This is just the beginning of our story. We're working on interactive demos, 
            detailed development timelines, and behind-the-scenes content.
          </p>
          <div className="bg-gray-800/50 rounded-lg p-8 border border-gray-600">
            <h3 className="text-2xl font-semibold text-white mb-4">What's Next</h3>
            <div className="grid md:grid-cols-2 gap-6 text-left">
              <div>
                <h4 className="text-lg font-semibold text-blue-400 mb-2">Interactive Timeline</h4>
                <p className="text-gray-400 text-sm">Complete development journey with code examples and challenges solved</p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-purple-400 mb-2">Live Code Demos</h4>
                <p className="text-gray-400 text-sm">Real-time examples of AI-human collaboration in action</p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-teal-400 mb-2">Technology Deep Dives</h4>
                <p className="text-gray-400 text-sm">Detailed explanations of React 19, DotPlayer, and dotstudioPRO integration</p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-yellow-400 mb-2">Behind the Scenes</h4>
                <p className="text-gray-400 text-sm">Development workflow videos and AI conversation examples</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VibeCodingStoryPage;

